import React from 'react';
import styled from 'styled-components';
import { Typography, Image, Avatar } from 'antd';
import { Container, Entity, EntityType, CustomPropertiesEntry, Maybe } from '../../../../../../../types.generated';
import { ANTD_GRAY, DATA_SYSTEM_NAME_KEY } from '../../../../constants';
import ContainerLink from './ContainerLink';
import {
    StyledRightOutlined,
    ParentNodesWrapper as ParentContainersWrapper,
    Ellipsis,
    StyledTooltip,
} from './ParentNodesView';
import ParentEntities from '../../../../../../search/filters/ParentEntities';
import { useTranslation } from '../../../../../../../i18n/useTranslation';

const LogoIcon = styled(Avatar)`
    .ant-avatar-string {
        position: static;
        display: flex;
        align-items: center;
        justify-content: center;
        transform: none !important;
    }
`;

const PreviewImage = styled(Image)`
    max-height: 17px;
    width: auto;
    object-fit: contain;
    background-color: transparent;
`;

const PlatformContentWrapper = styled.div`
    gap: 8px;
    display: flex;
    align-items: center;
    margin: 0 8px 6px 0;
    flex-wrap: wrap;
    flex: 1;

    @media (max-width: 768px) {
        margin-right: 0;
    }
`;

const PlatformText = styled(Typography.Text)`
    color: ${ANTD_GRAY[7]};
    white-space: nowrap;
`;

const PlatformDivider = styled.div`
    height: 24px;
    display: inline-block;
    vertical-align: text-top;
    border-right: 1px solid ${ANTD_GRAY[4]};
`;

export function getParentContainerNames(containers?: Maybe<Container>[] | null) {
    let parentNames = '';
    if (containers) {
        [...containers].reverse().forEach((container, index) => {
            if (container?.properties) {
                if (index !== 0) {
                    parentNames += ' > ';
                }
                parentNames += container.properties.name;
            }
        });
    }
    return parentNames;
}

interface Props {
    platformName?: string;
    platformLogoUrl?: Maybe<string>;
    platformNames?: Maybe<string | undefined>[];
    platformLogoUrls?: Maybe<string | undefined>[];
    entityLogoComponent?: JSX.Element;
    instanceId?: string;
    typeIcon?: JSX.Element;
    entityType?: string;
    parentContainers?: Maybe<Container>[] | null;
    parentEntities?: Entity[] | null;
    parentContainersRef: React.RefObject<HTMLDivElement>;
    areContainersTruncated: boolean;
    type?: string;
    subType?: string;
    customProperties?: Maybe<CustomPropertiesEntry[]>;
}

function PlatformContentView(props: Props) {
    const { t } = useTranslation();
    const {
        parentEntities,
        platformName,
        platformLogoUrl,
        platformNames,
        platformLogoUrls,
        entityLogoComponent,
        instanceId,
        typeIcon,
        entityType,
        parentContainers,
        parentContainersRef,
        areContainersTruncated,
        type = '',
        subType,
        customProperties,
    } = props;

    const directParentContainer = parentContainers && parentContainers[0];
    const remainingParentContainers = parentContainers && parentContainers.slice(1, parentContainers.length);
    const isDatahub = platformName === 'DataHub';

    // Mapping entity types to their corresponding names
    const entityTypeMap = {
        [EntityType.GlossaryTerm]: 'Glossary Term',
        [EntityType.GlossaryNode]: 'Term Group',
    };

    const isGlossaryTerm = entityType === 'Glossary Term';

    const entityTypeName = entityTypeMap[type] || entityType;

    // Extract data_system_name from custom properties
    const dataSystemName = customProperties?.find((prop) => prop.key === DATA_SYSTEM_NAME_KEY)?.value;
    const displayInstanceId = dataSystemName || instanceId;

    return (
        <PlatformContentWrapper>
            {typeIcon && <LogoIcon className="f-avatar-small f-avatar-black">{typeIcon}</LogoIcon>}
            <PlatformText className="f-text-medium-content f-color-dark-black-s70">{t(entityTypeName)}</PlatformText>
            {isGlossaryTerm && subType && (
                <>
                    <PlatformDivider />
                    <PlatformText className="f-text-medium-content f-color-dark-black-s70">{subType}</PlatformText>
                </>
            )}

            {(!!platformName || !!instanceId || !!parentContainers?.length || !!parentEntities?.length) && (
                <PlatformDivider />
            )}
            {platformName && !isDatahub && (
                <LogoIcon className="f-avatar-small f-avatar-black">
                    {!platformLogoUrl && !platformLogoUrls && entityLogoComponent}
                    {!!platformLogoUrl && !platformLogoUrls && (
                        <PreviewImage preview={false} src={platformLogoUrl} alt={platformName} />
                    )}
                    {!!platformLogoUrls &&
                        platformLogoUrls.slice(0, 2).map((platformLogoUrlsEntry) => (
                            <>
                                <PreviewImage preview={false} src={platformLogoUrlsEntry || ''} alt={platformName} />
                            </>
                        ))}
                </LogoIcon>
            )}
            {!isDatahub && (
                <PlatformText className="f-text-medium-content f-color-dark-black-s100">
                    {platformNames ? platformNames.join(' & ') : platformName}
                    {(directParentContainer || displayInstanceId) && <StyledRightOutlined data-testid="right-arrow" />}
                </PlatformText>
            )}
            {displayInstanceId && (
                <PlatformText className="f-text-medium-content f-color-dark-black-s100">
                    {displayInstanceId}
                    {directParentContainer && <StyledRightOutlined data-testid="right-arrow" />}
                </PlatformText>
            )}
            <StyledTooltip
                title={getParentContainerNames(parentContainers)}
                overlayStyle={areContainersTruncated ? {} : { display: 'none' }}
            >
                {areContainersTruncated && <Ellipsis>...</Ellipsis>}
                <ParentContainersWrapper ref={parentContainersRef}>
                    {remainingParentContainers &&
                        remainingParentContainers.map((container, index) => (
                            <span key={container?.urn}>
                                <ContainerLink container={container} />
                                {index > 0 && <StyledRightOutlined data-testid="right-arrow" />}
                            </span>
                        ))}
                </ParentContainersWrapper>
                {/* TODO: Hiding the ContainerLink for now */}
                {/* {directParentContainer && <ContainerLink container={directParentContainer} />} */}
            </StyledTooltip>
            <ParentEntities parentEntities={parentEntities || []} numVisible={3} />
        </PlatformContentWrapper>
    );
}

export default PlatformContentView;
